"use client";

import Script from "next/script";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { env } from "@/env";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      theme?: "light" | "dark" | "auto";
      language?: string;
      "error-callback"?: (errorCode: string) => boolean;
    },
  ) => string | undefined;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const TURNSTILE_SITE_KEY = env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

// Name of the hidden input Turnstile adds to the enclosing <form>
export const TURNSTILE_FIELD = "cf-turnstile-response";

/**
 * Cloudflare Turnstile widget (managed mode). Usually passes on its own and
 * only asks for a click when the traffic looks suspicious. Tokens are
 * single-use, so remount it (change `key`) after every submit.
 */
export default function Turnstile({ action }: { action: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(false);
  // Site locales (pl, en, uk) are all supported by Turnstile as-is
  const language = useLocale();

  useEffect(() => {
    const turnstile = window.turnstile;
    const container = containerRef.current;
    if (!scriptReady || !turnstile || !container || !TURNSTILE_SITE_KEY) {
      return;
    }

    const widgetId = turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      action,
      theme: "dark",
      language,
      // Without this Turnstile throws on error (e.g. 110200 = domain not in
      // the widget's hostname list). The widget shows the error itself, and
      // the form refuses to submit without a token.
      "error-callback": (errorCode) => {
        console.warn(`[Turnstile] Widget error ${errorCode}`);
        return true;
      },
    });
    return () => {
      if (widgetId) turnstile.remove(widgetId);
    };
  }, [scriptReady, action, language]);

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        onReady={() => setScriptReady(true)}
      />
      <div ref={containerRef} />
    </>
  );
}
